import * as React from 'react';
import { ConfirmDialog, DialogContainer, NotifyDialog } from './Dialogs';
import { Button } from './Button';
import {
  BulkDeleteState,
  BulkDeleteTarget,
} from '../pages/PlaceListPage.logic';
import './PlaceBulkDeleteDialog.css';

interface PlaceBulkDeleteDialogProps {
  state: BulkDeleteState;
  onConfirm: (targets: BulkDeleteTarget[]) => void;
  onCancelConfirm: () => void;
  onRequestCancel: () => void;
  onCloseResult: () => void;
}

/** 中止のボタンの文言。中止を受け付けたあとは、今の1件が終わるまで待っていることを示す */
function getCancelButtonLabel(cancelRequested: boolean): string {
  if (cancelRequested) {
    return '中止します…';
  }
  return '中止';
}

/** 結果のダイアログの題名。中止したのか、失敗があったのかで分ける */
function getResultTitle(cancelled: boolean): string {
  if (cancelled) {
    return '削除を中止しました';
  }
  return '一部の物件を削除できませんでした';
}

// 削除の前後と進み具合を、同じ場所で確認できるようにする
export const PlaceBulkDeleteDialog: React.FC<PlaceBulkDeleteDialogProps> = function (
  props
) {
  const state = props.state;

  if (state.kind === 'confirming') {
    return (
      <ConfirmDialog
        open={true}
        danger={true}
        title="物件の削除"
        yesLabel="削除"
        message={
          <div className="Dialog-message">
            <p>{`${state.targets.length}件の物件を削除します。削除した物件は元に戻せません。`}</p>
            <ul className="PlaceBulkDeleteDialog-name_list">
              {state.targets.map((target) => (
                <li key={target.id}>{target.name}</li>
              ))}
            </ul>
          </div>
        }
        onYes={() => props.onConfirm(state.targets)}
        onNo={() => props.onCancelConfirm()}
      />
    );
  }

  if (state.kind === 'deleting') {
    const currentTarget = state.targets[state.finishedCount];

    return (
      <div role="status" aria-live="polite">
        <DialogContainer
          open={true}
          title="物件を削除しています"
          message={
            <div className="Dialog-message">
              <p>
                {`削除中 ${state.finishedCount + 1} / ${
                  state.targets.length
                } 件`}
              </p>
              <p className="Dialog-text_wrap">{currentTarget.name}</p>
              <p>中止すると、今削除している物件が終わってから止まります</p>
            </div>
          }
        >
          <Button
            mini={true}
            color="white"
            disabled={state.cancelRequested}
            onClick={() => props.onRequestCancel()}
          >
            {getCancelButtonLabel(state.cancelRequested)}
          </Button>
        </DialogContainer>
      </div>
    );
  }

  if (state.kind === 'finished') {
    const result = state.result;

    return (
      <NotifyDialog
        open={true}
        title={getResultTitle(result.cancelled)}
        message={
          <div className="Dialog-message">
            <p>{`削除した物件: ${result.deletedCount}件`}</p>
            {result.failedTargets.length > 0 && (
              <React.Fragment>
                <p>
                  {`削除できなかった物件: ${result.failedTargets.length}件`}
                </p>
                <ul className="PlaceBulkDeleteDialog-name_list">
                  {result.failedTargets.map((target) => (
                    <li key={target.id}>{target.name}</li>
                  ))}
                </ul>
              </React.Fragment>
            )}
            {result.skippedTargets.length > 0 && (
              <p>{`実行しなかった物件: ${result.skippedTargets.length}件`}</p>
            )}
          </div>
        }
        onYes={() => props.onCloseResult()}
      />
    );
  }

  return null;
};
